/**
 * New Year badges — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearBadgesPack: ToilePack = {
    slug: 'new-year-badges',
    category: 'season',
    name: 'New Year badges',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'seal', label: 'Round seal', subject: 'A round badge lettered "HAPPY NEW YEAR" around the rim with a clock at the centre, isolated'},
        {key: 'ribbon', label: 'Ribbon badge', subject: 'A ribbon badge lettered "EST. JAN 1" with stars, isolated'},
        {key: 'stamp', label: 'Postmark stamp', subject: 'A round postmark-style stamp lettered "MIDNIGHT" with a firework, isolated'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['for a bottle label', 'in a single foil colour', 'as a smaller lapel pin'],
    directionHint: 'try: for a bottle label',
    seo: {
        title: 'New Year Badge: Seal, Ribbon, Stamp',
        metaDescription: 'Nine new year badge and logo designs — round seal, ribbon badge, stamp — lettered, one colour, isolated, in three styles.',
        h1: 'New year badge: seal, ribbon, stamp',
        tagline: 'Nine badges, three midnight subjects, three styles each.',
        intro: 'Lettered emblem marks for the midnight brand: a round seal, a ribbon badge, a stamp, each isolated and built in a single colour so it reads as a mark, not a scene. Ask for a starburst badge, a year-numeral crest, or your own wording, and the same three styles carry it.',
        useCases: [
            'a small-business New Year sale badge',
            'a logo mark for a party or event',
            'a printed sticker or seal for packaging',
            'a merch badge for a shirt or tote',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine emblem-style badges — a round seal, a ribbon badge, a stamp — lettered and single-colour, in three styles.'},
            {q: 'Can I use this as an actual business logo?', a: 'The pack renders a general emblem design, not a registered trademark; it works well as a seasonal mark, but a permanent brand logo is worth having designed and cleared separately.'},
            {q: 'Can I add my own business name or wording?', a: 'The pack renders the wording as shown; swapping in your own line is a quick edit after downloading.'},
            {q: 'What format do I get the badge in?', a: 'Each renders as a single image, isolated on a plain background — good for placing on packaging, stickers, or merch mockups.'},
            {q: "Can I use this for a product or shop I'm selling from?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
